import { useState } from 'react';
import { 
  signInWithEmailAndPassword,       // Firebase function to log in with email/password
  createUserWithEmailAndPassword,   // Firebase function to create new account
  signInWithPopup,                  // Firebase function for Google sign-in
  GoogleAuthProvider,               // Google sign-in provider
  updateProfile,                     // Firebase function to update user profile
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import { auth } from '../firebase';
import { theme, buttonStyles, inputStyles, typography, spacing, mergeStyles } from '../styles/theme';
import { trackUserEvent } from '../analytics';

// Add this helper function at the top of Auth.js (before the component)
const getFriendlyErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Invalid email or password. Please try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/too-many-requests':
      return 'Too many failed login attempts. Please try again later.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled.';
    default:
      return 'An error occurred. Please try again.';
  }
};

export default function Auth({ onSuccess }) {
  // State variables to track what the user types and whether they're signing up or logging in
  const [email, setEmail] = useState('');           // Stores email input
  const [password, setPassword] = useState('');     // Stores password input
  const [firstName, setFirstName] = useState('');   // Stores first name input (for sign-up)
  const [lastName, setLastName] = useState('');     // Stores last name input (for sign-up)
  const [isSignUp, setIsSignUp] = useState(false);  // true = signup form, false = login form
  const [error, setError] = useState('');           // Stores error messages to show user
  const [showForgotPassword, setShowForgotPassword] = useState(false); // Toggle forgot password view
  const [resetEmail, setResetEmail] = useState(''); // Email input for password reset
  const [resetSuccess, setResetSuccess] = useState(false); // Success message for password reset
  const [loading, setLoading] = useState(false);    // Loading state for async actions

  // This function runs when user submits the email/password form
  const handleEmailAuth = async (e) => {
    e.preventDefault(); // Prevents page refresh when form submits
    setError(''); // Clear any previous errors
    
    try {
      if (isSignUp) {
        // Create new account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Update profile with first and last name
        await updateProfile(userCredential.user, {
          displayName: `${firstName} ${lastName}`.trim() // Combine names, trim extra spaces
        });

        // Note: The user object doesn't automatically update in the component
        // Force a refresh by calling onSuccess which will trigger auth state listener
      } else {
        // Log in existing account
        await signInWithEmailAndPassword(auth, email, password);
      }
      if (onSuccess) onSuccess(); // Close modal on success
    } catch (error) {
      // Show user-friendly error message
      console.error('Authentication error:', error);
      setError(getFriendlyErrorMessage(error.code));
    }
  };

  // This function runs when user clicks "Sign in with Google" button
  const handleSocialSignIn = async (provider) => {
    try {
      await signInWithPopup(auth, provider);
      if (onSuccess) onSuccess();
    } catch (error) {
      // Handle account-exists error
      if (error.code === 'auth/account-exists-with-different-credential') {
        const email = error.customData?.email || error.email;
        
        try {
          // Check what sign-in methods exist for this email
          const methods = await fetchSignInMethodsForEmail(auth, email);
          
          if (methods.includes('password')) {
            setError(`An account with ${email} already exists. Please sign in with your email and password first. You can then link your Google account in Settings.`);
          } else if (methods.includes('google.com')) {
            setError(`This account is already linked with Google. Please use the Google sign-in button.`);
          } else {
            setError(`An account with ${email} already exists using a different sign-in method.`);
          }
        } catch (fetchError) {
          setError(`An account with this email already exists. Please sign in with your original method.`);
        }
      } else if (error.code === 'auth/popup-closed-by-user') {
        // User closed popup - don't show error
        return;
      } else {
        setError(getFriendlyErrorMessage(error.code));
      }
    }
  };

  // Function to determine which provider to use based on the method
  const handleProviderSignIn = (method) => {
    let provider;
  
    // Switch statement lets you add more providers easily in the future
    switch (method) {
        case 'google':
        provider = new GoogleAuthProvider();
        break;
        case 'facebook':
        // provider = new FacebookAuthProvider(); // Example for future
        // break;
        case 'twitter':
        // provider = new TwitterAuthProvider(); // Example for future
        // break;
        default:
        setError('Invalid sign-in method');
        return;
    }
  
    // Call the generic sign-in function with the chosen provider
    handleSocialSignIn(provider);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
      trackUserEvent('password_reset_requested');
    } catch (error) {
      console.error('Password reset error:', error);
      setError(getFriendlyErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{
        marginTop: 0,
        color: theme.text.primary,
        textAlign: 'center',
        fontFamily: typography.fontFamily.primary,
        fontSize: typography.fontSize.xl
      }}>
        {isSignUp ? 'Create Account' : 'Log In'}
      </h2>
      
      {/* Show error message if there is one */}
      {error && (
        <p style={{
            color: theme.status.error,
            backgroundColor: '#ffe5e5',
            padding: spacing.md,
            borderRadius: '6px',
            fontSize: typography.fontSize.sm,
        }}>
            {error}
        </p>
      )}
      
      {/* Email/Password Form */}
      <form onSubmit={handleEmailAuth} style={{ marginBottom: spacing.xl }}>
        {/* Show name fields only during sign-up */}
        {isSignUp && (
            <>
                <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)} // Updates first name state as user types
                    style={inputStyles.base}
                    required
                />
                <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)} // Updates last name state as user types
                    style={{...inputStyles.base, marginTop: spacing.md}}
                    required
                />
            </>
        )}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)} // Updates email state as user types
          style={{...inputStyles.base, marginTop: isSignUp ? spacing.md : 0}}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)} // Updates password state as user types
          style={{...inputStyles.base, marginTop: spacing.md}}
          required
        />
        <button 
          type="submit"
          style={mergeStyles(
            buttonStyles.base,
            buttonStyles.primary,
            { marginTop: spacing.md, width: '100%' }
        )}
          onMouseOver={(e) => e.target.style.backgroundColor = theme.brand.primaryHover}
          onMouseOut={(e) => e.target.style.backgroundColor = theme.brand.primary}
        >
          {isSignUp ? 'Sign Up' : 'Log In'}
        </button>
      </form>
      
      {/* Google Sign-In Button */}
      <button 
        onClick={() => handleProviderSignIn('google')}
        style={mergeStyles(
            buttonStyles.base,
            buttonStyles.dark,
            { marginBottom: spacing.md, width: '100%' }
        )}
      >
        Sign in with Google
      </button>
      
      {/* Toggle between Sign Up and Log In */}
      <button 
        onClick={() => setIsSignUp(!isSignUp)}
        style={mergeStyles(
            buttonStyles.base,
            buttonStyles.secondary,
            {
                width: '100%',
                fontSize: typography.fontSize.sm,
                padding: spacing.md
            }
        )}
      >
        {isSignUp ? 'Already have an account? Log In' : 'Need an account? Sign Up'}
      </button>
      {!showForgotPassword && (
            <div style={{ textAlign: 'center', marginTop: spacing.md }}>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setError('');
                  setResetEmail(email); // Pre-fill with current email
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.brand.primary,
                  fontSize: typography.fontSize.sm,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Forgot your password?
              </button>
            </div>
          )}

          {/* ← ADD: Forgot Password Form */}
          {showForgotPassword && !resetSuccess && (
            <div style={{
              marginTop: spacing.lg,
              padding: spacing.lg,
              backgroundColor: theme.bg.secondary,
              borderRadius: '8px',
              border: `2px solid ${theme.brand.primary}`,
            }}>
              <h3 style={{
                margin: 0,
                marginBottom: spacing.sm,
                color: theme.text.primary,
                fontSize: typography.fontSize.lg,
              }}>
                Reset Password
              </h3>
              <p style={{
                margin: 0,
                marginBottom: spacing.md,
                color: theme.text.secondary,
                fontSize: typography.fontSize.sm,
              }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Email"
                style={{
                  width: '100%',
                  padding: spacing.md,
                  marginBottom: spacing.md,
                  border: `2px solid ${theme.border.medium}`,
                  borderRadius: '8px',
                  fontSize: typography.fontSize.base,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <button
                  onClick={handlePasswordReset}
                  disabled={loading || !resetEmail}
                  style={mergeStyles(
                    buttonStyles.base,
                    buttonStyles.primary,
                    { flex: 1 }
                  )}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setError('');
                  }}
                  disabled={loading}
                  style={mergeStyles(
                    buttonStyles.base,
                    buttonStyles.secondary,
                    { flex: 1 },
                    loading && { opacity: 0.6, cursor: 'not-allowed' }
                  )}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ← ADD: Success Message */}
          {resetSuccess && (
            <div style={{
              marginTop: spacing.lg,
              padding: spacing.lg,
              backgroundColor: '#d1fae5',
              border: '2px solid #10b981',
              borderRadius: '8px',
            }}>
              <p style={{
                margin: 0,
                color: '#065f46',
                fontSize: typography.fontSize.sm,
              }}>
                ✅ Password reset email sent! Check your inbox and follow the link to reset your password.
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetSuccess(false);
                  setResetEmail('');
                }}
                style={{
                  marginTop: spacing.md,
                  background: 'none',
                  border: 'none',
                  color: theme.brand.primary,
                  fontSize: typography.fontSize.sm,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Back to login
              </button>
            </div>
          )}
    </div>
  );
}